<template>
  <div class="shop-product-details">
    <div class="shop-product-details__header">
      <Carousel
        class="shop-product-details__header-wrapper"
        :per-page="1"
        pagination-color="white"
        pagination-position="bottom-overlay"
        :pagination-padding="5"
        :navigation-enabled="false"
        :autoplay-timeout="4000"
        :key="item.product.images.map(i => i.url).join('-')"
        autoplay
        loop
      >
        <Slide
          v-for="(image, index) in item.product.images"
          :key="index"
          class="shop-product-details__image"
        >
          <img class="content" :src="image.url" ref="items" />
          <span class="shop-product-details__image-disclaimer">{{
            $tc('imagesOnlyAsIllustration')
          }}</span>
        </Slide>
        <Slide
          v-if="!item.product.images.length"
          class="shop-product-details__image shop-product-details__image-placeholder"
        >
          <div class="content">
            <FontAwesomeIcon :icon="['fal', 'box-alt']" size="4x" />
          </div>
        </Slide>
      </Carousel>
      <div class="shop-product-details__attachments" v-if="item.product.attachments.length">
        <div class="shop-product-details__attachments-label">{{ $tc('attachment', 2) }}:</div>
        <div class="shop-product-details__attachments-list">
          <FileComponent :value="item.product.attachments" readonly />
          <ScrollIndicator :value="['top', 'bottom']" />
        </div>
      </div>
      <div
        class="shop-product-details__products"
        v-if="item.product.products && item.product.products.length"
      >
        <div class="shop-product-details__products-label">{{ $tc('productBundleContains') }}:</div>
        <div class="shop-product-details__products-list">
          <ProductCard
            v-for="(product, index) in item.product.products"
            :key="index"
            show-image
            :value="{ product: product }"
            hidePrice
          >
            <template #actions>
              <ButtonComponent
                :label="$t('readMore')"
                @click="$emit('open:modal', { product })"
                style="margin-left: auto; font-size: 0.7em"
              />
            </template>
          </ProductCard>
          <ScrollIndicator :value="['top', 'bottom']" />
        </div>
      </div>
    </div>

    <div class="shop-product-details__body">
      <div class="shop-product-details__body-wrapper">
        <div class="shop-product-details__name">{{ item.product.name }}</div>
        <div
          v-if="item.product.description"
          class="shop-product-details__description"
          v-html="item.product.description"
        ></div>

        <slot name="body-bottom" />
        <ScrollIndicator :value="['top', 'bottom']" />
      </div>
    </div>
  </div>
</template>

<script>
import { Carousel, Slide } from 'vue-carousel'
import { ButtonComponent } from 'vue-elder-button'
import { FileComponent } from 'vue-elder-file'
import { path } from 'ramda'
import MediumZoom from 'medium-zoom'
import ScrollIndicator from '@kvass/scroll-indicator'
import ProductCard from './Card'

export default {
  props: {
    value: Object,
  },
  watch: {
    value: {
      handler: 'attach',
      immediate: true,
    },
  },
  data() {
    return {
      backgroundColor: null,
      zoomer: MediumZoom([], {
        scrollOffset: 20,
        background: 'rgba(0, 0, 0, .8)',
      }),
    }
  },
  computed: {
    item() {
      return (
        this.value || {
          product: {
            name: this.$t('noShopDefaultProduct'),
            images: [],
            attachments: [],
            description: `<span style="opacity: .5">Velg et alternativ fra listen for å oppgradere <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="long-arrow-alt-right" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" class="svg-inline--fa fa-long-arrow-alt-right fa-w-14" style="margin-left: 5px;"><path fill="currentColor" d="M313.941 216H12c-6.627 0-12 5.373-12 12v56c0 6.627 5.373 12 12 12h301.941v46.059c0 21.382 25.851 32.09 40.971 16.971l86.059-86.059c9.373-9.373 9.373-24.569 0-33.941l-86.059-86.059c-15.119-15.119-40.971-4.411-40.971 16.971V216z" class=""></path></svg></span>`,
          },
        }
      )
    },
  },
  methods: {
    path(key) {
      return path(key.split('.'), this)
    },
    attach() {
      this.detach()
      this.$nextTick(() => this.zoomer.attach(this.$refs.items))
    },
    detach() {
      if (!this.zoomer) return
      this.zoomer.detach()
    },
  },
  beforeDestroy() {
    this.detach()
  },
  components: {
    Carousel,
    Slide,
    ButtonComponent,
    FileComponent,
    ScrollIndicator,
    ProductCard,
  },
}
</script>

<style lang="scss">
@import '../styles/_variables';

.shop-product-details {
  $footerHeight: 109px;
  $navHeight: 80px;
  $height: calc(100vh - calc(#{$footerHeight} + #{$navHeight}));

  background-color: white;
  border-radius: var(--border-radius);

  display: grid;
  grid-template-columns: 2fr minmax(350px, 1fr);
  grid-gap: 3rem;
  align-items: flex-start;

  @media (max-width: 1400px) {
    grid-template-columns: 1fr;
  }

  &__products {
    @media (max-width: 1400px) {
      order: 2;
      margin-bottom: 0;
    }

    &-list {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      max-height: 350px;
      overflow-y: auto;

      @include scrollShadow();
    }

    &-label {
      margin-bottom: 0.5rem;
      font-weight: bold;
    }
  }

  &__header {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    height: 100%;
    @media (max-width: 1400px) {
      display: contents;
    }

    &-wrapper {
      border-radius: var(--border-radius);
      overflow: hidden;
      border: 1px solid $border-color;
    }
  }

  &__body {
    position: relative;
    @media (max-width: 1400px) {
      order: 1;
    }
  }

  &__name {
    font-size: 1.5em;
    font-weight: bold;
  }

  &__description {
    & > *:first-child {
      margin-top: 0;
    }

    & > *:last-child {
      margin-bottom: 0;
    }
  }

  &__price {
    font-size: 1.5em;
    font-weight: bold;
  }

  &__image {
    @include aspect-ratio(16, 9);
    margin: 0 auto;

    .content {
      max-width: 100%;
      height: 100%;
      margin: 0 auto;
      padding: 1rem;

      @media (max-width: $shop-responsive) {
        padding: 0;
      }
    }

    &-placeholder {
      background-color: rgba(black, 0.03);

      .content {
        display: flex;
        align-items: center;
        justify-content: center;
      }

      svg {
        opacity: 0.1;
      }
    }

    &-disclaimer {
      font-size: 0.6em;
      white-space: nowrap;
      padding: 0.25rem 0.5rem;
      background-color: rgba(255, 255, 255, 0.4);
      backdrop-filter: blur(8px);
      border-radius: 0 var(--border-radius) var(--border-radius) 0;
      position: absolute;
      bottom: 0.25rem;
      left: 0.25rem;
      z-index: 1005;
    }
  }

  &__actions {
    margin-top: auto;
  }

  &__attachments {
    @media (max-width: 1400px) {
      order: 2;
      margin-bottom: 0;
    }

    &-list {
      position: relative;
      &-wrapper {
        height: 100%;
        max-height: 250px;
        overflow-y: auto;
      }
    }

    &-label {
      margin-bottom: 0.5rem;
      font-weight: bold;
    }
  }
  .scroll-indicator__arrow {
    border: 1px solid #dedede;
    border-radius: 20px 0 0 20px;
    border-right: none;
    width: 30px;
    height: 30px;
  }

  .shop-floorplan-wrapper {
    margin-top: 1rem;
    flex-grow: initial;

    & > label {
      display: block;
      margin-bottom: 0.5rem;
    }
  }

  .VueCarousel-dot,
  .VueCarousel-dot-container {
    margin-top: 0 !important;
  }

  .VueCarousel-dot--active {
    background-color: css-lighten('primary', 15%) !important;
  }

  .VueCarousel-dot-container {
    background-color: css-darken('grey', 55%);
    padding: 0.15em;
    border-radius: 50px;
    margin-bottom: 1.5rem;
    display: inline-flex;
  }
}
</style>
